import React, { useState } from "react";
import api from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Mail, Send, MapPin } from "lucide-react";

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    body: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.body.trim().length < 10) {
      toast.error("Merci de remplir tous les champs (message ≥ 10 caractères).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé — nous vous répondons sous 48h");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="contact-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-800 rounded-full px-3 py-1 text-xs font-medium mb-3">
          <Mail className="w-3.5 h-3.5" /> Service client MySwimmingLessons
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          Nous contacter
        </h1>
        <p className="text-slate-600 mt-2">
          Une question, un signalement, une suggestion ? Notre équipe répond sous 48h ouvrées.
        </p>
      </div>

      {sent ? (
        <div className="card-soft p-8 text-center" data-testid="contact-sent">
          <div className="text-5xl mb-2">✅</div>
          <h2 className="font-bold text-2xl text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>Message bien reçu</h2>
          <p className="text-slate-600">Nous revenons vers vous au plus vite sur <strong>{form.email}</strong>.</p>
          <div className="mt-4 mx-auto max-w-md bg-amber-50 border border-amber-200 rounded-xl p-3 text-left text-sm text-amber-900" data-testid="spam-folder-notice">
            ⚠️ <strong>Pas de réponse sous 48h ?</strong> Pensez à vérifier votre dossier <em>« Courriers indésirables »</em> ou <em>« Spam »</em>.
            Notre adresse <code className="bg-amber-100 px-1 rounded text-amber-900">contact@myswimminglessons.fr</code> peut être filtrée par certains opérateurs.
          </div>
          <Button className="mt-4 rounded-full bg-sky-700 hover:bg-sky-600" onClick={() => { setSent(false); setForm({ ...form, subject: "", body: "" }); }}>
            Écrire un autre message
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Nom complet <span className="text-red-500">*</span></Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label>Sujet <span className="text-red-500">*</span></Label>
            <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Ex: Problème de paiement, Signalement, Partenariat…" data-testid="contact-subject" />
          </div>
          <div>
            <Label>Message <span className="text-red-500">*</span></Label>
            <Textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Décrivez-nous votre demande en détail."
              data-testid="contact-body" />
            <p className="text-xs text-slate-500 mt-1">Min. 10 caractères.</p>
          </div>
          <Button type="submit" disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="contact-submit">
            <Send className="w-4 h-4 mr-1" />{busy ? "Envoi…" : "Envoyer mon message"}
          </Button>
          <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="spam-folder-notice-form">
            En l'absence de réponse sous 48h, vérifiez votre dossier <strong>Courriers indésirables / Spam</strong>.
          </p>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="card-soft p-5 flex items-start gap-3">
          <Mail className="w-5 h-5 text-sky-700 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">Email</div>
            <a href="mailto:contact@myswimminglessons.fr" className="text-sky-700 text-sm hover:underline">contact@myswimminglessons.fr</a>
          </div>
        </div>
        <div className="card-soft p-5 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-sky-700 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">Adresse</div>
            <p className="text-sm text-slate-600">SAS ALEX SWIM COACH — 11 allée des acacias, 92310 SÈVRES</p>
          </div>
        </div>
      </div>
    </div>
  );
}
